import { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { FiBox, FiDownload, FiPlus, FiRefreshCw } from 'react-icons/fi';
import type { AuthFetch, StockMovement, User, UserStock } from '../../types';
import { Button, ModalBackdrop, Select, TextInput } from '../ui';
import { cn } from '../../lib/cn';

interface StockReportPageProps {
  authFetch: AuthFetch;
}

const TYPES = ['CTO', 'CEP', 'CEO'] as const;
const REASON_LABEL: Record<string, string> = { entrada: 'Entrada', montagem: 'Montagem', ajuste: 'Ajuste' };

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function StockReportPage({ authFetch }: StockReportPageProps) {
  const [subTab, setSubTab] = useState<'balances' | 'movements'>('balances');
  const [techs, setTechs] = useState<User[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(false);
  const [fUser, setFUser] = useState('');
  const [fType, setFType] = useState('');
  const [fReason, setFReason] = useState('');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  // Lançar estoque para um técnico (modal).
  const [stockTarget, setStockTarget] = useState<User | null>(null);
  const [stockEntry, setStockEntry] = useState({ CTO: '', CEP: '', CEO: '' });
  const [targetMovements, setTargetMovements] = useState<StockMovement[]>([]);

  const totals = useMemo(() => {
    const acc = { CTO: 0, CEP: 0, CEO: 0 };
    techs.forEach((u) => {
      acc.CTO += u.stock?.CTO || 0;
      acc.CEP += u.stock?.CEP || 0;
      acc.CEO += u.stock?.CEO || 0;
    });
    return acc;
  }, [techs]);

  const loadUsers = () => {
    authFetch<User[]>('/auth/users')
      .then((u) => setTechs((Array.isArray(u) ? u : []).filter((x) => x.type !== 'admin')))
      .catch(() => setTechs([]));
  };

  const loadMovements = () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '1000' });
    if (fFrom) params.set('from', new Date(fFrom + 'T00:00:00').toISOString());
    if (fTo) params.set('to', new Date(fTo + 'T23:59:59').toISOString());
    authFetch<StockMovement[]>(`/auth/stock/movements?${params.toString()}`)
      .then((m) => setMovements(Array.isArray(m) ? m : []))
      .catch(() => setMovements([]))
      .finally(() => setLoading(false));
  };

  const reload = () => {
    loadUsers();
    loadMovements();
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recarrega as movimentações quando o intervalo de datas muda (filtro no backend).
  useEffect(() => {
    loadMovements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fFrom, fTo]);

  // Carrega o histórico recente do técnico selecionado no modal.
  useEffect(() => {
    if (!stockTarget) {
      setTargetMovements([]);
      return;
    }
    authFetch<StockMovement[]>(`/auth/users/${encodeURIComponent(stockTarget.id)}/stock/movements?limit=30`)
      .then((d) => setTargetMovements(Array.isArray(d) ? d : []))
      .catch(() => setTargetMovements([]));
  }, [stockTarget, authFetch]);

  const submitStock = async () => {
    if (!stockTarget) return;
    const body: Record<string, number> = {};
    TYPES.forEach((t) => {
      const n = parseInt(stockEntry[t], 10);
      if (Number.isFinite(n) && n !== 0) body[t] = n;
    });
    if (Object.keys(body).length === 0) return window.alert('Informe ao menos uma quantidade a lançar.');
    try {
      const resp = await authFetch<{ stock: UserStock }>(`/auth/users/${encodeURIComponent(stockTarget.id)}/stock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      setStockEntry({ CTO: '', CEP: '', CEO: '' });
      // Mantém o modal aberto: atualiza saldo do alvo + a lista de técnicos + histórico (via efeito).
      setStockTarget((prev) => (prev ? { ...prev, stock: resp.stock } : prev));
      setTechs((prev) => prev.map((u) => (u.id === stockTarget.id ? { ...u, stock: resp.stock } : u)));
      loadMovements();
    } catch (err) {
      window.alert((err as Error).message || 'Erro ao lançar estoque');
    }
  };

  const filteredMovements = useMemo(() => {
    return movements.filter((m) => {
      const userOk = !fUser || m.username === fUser;
      const typeOk = !fType || m.type === fType;
      const reasonOk = !fReason || m.reason === fReason;
      return userOk && typeOk && reasonOk;
    });
  }, [movements, fUser, fType, fReason]);

  // Resumo do período (sobre as movimentações filtradas).
  const summary = useMemo(() => {
    let entradas = 0;
    let ajustes = 0;
    const consumo = { CTO: 0, CEP: 0, CEO: 0, total: 0 };
    filteredMovements.forEach((m) => {
      if (m.reason === 'montagem') {
        const q = Math.abs(m.delta);
        consumo[m.type] += q;
        consumo.total += q;
      } else if (m.reason === 'entrada') {
        entradas += m.delta;
      } else {
        ajustes += m.delta;
      }
    });
    return { entradas, ajustes, consumo };
  }, [filteredMovements]);

  const exportBalancesCsv = () => {
    const csv = Papa.unparse(
      techs.map((u) => ({
        tecnico: u.username,
        CTO: u.stock?.CTO || 0,
        CEP: u.stock?.CEP || 0,
        CEO: u.stock?.CEO || 0,
        total: (u.stock?.CTO || 0) + (u.stock?.CEP || 0) + (u.stock?.CEO || 0)
      }))
    );
    downloadCsv(csv, `estoque_saldos_${Date.now()}.csv`);
  };

  const exportMovementsCsv = () => {
    const csv = Papa.unparse(
      filteredMovements.map((m) => ({
        data: fmtDateTime(m.createdAt),
        tecnico: m.username || '',
        tipo: m.type,
        quantidade: m.delta,
        motivo: REASON_LABEL[m.reason] || m.reason,
        caixa: m.boxId || '',
        saldo_apos: m.balanceAfter,
        lancado_por: m.changedBy || ''
      }))
    );
    downloadCsv(csv, `estoque_movimentacoes_${Date.now()}.csv`);
  };

  const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const th = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap';
  const td = 'px-4 py-2.5 text-sm text-slate-700 whitespace-nowrap';

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3 sm:p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-lg font-bold text-slate-800">
          <FiBox size={20} />
          Estoque
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1">
            <button
              onClick={() => setSubTab('balances')}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-semibold transition',
                subTab === 'balances' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              )}
            >
              Saldos
            </button>
            <button
              onClick={() => setSubTab('movements')}
              className={cn(
                'rounded-lg px-3 py-1.5 text-sm font-semibold transition',
                subTab === 'movements' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-100'
              )}
            >
              Movimentações
            </button>
          </div>
          <Button variant="ghost" size="sm" onClick={reload} title="Recarregar">
            <FiRefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </Button>
          <Button variant="success" size="sm" onClick={subTab === 'balances' ? exportBalancesCsv : exportMovementsCsv}>
            <FiDownload size={16} />
            <span>Exportar CSV</span>
          </Button>
        </div>
      </div>

      {subTab === 'balances' ? (
        <>
          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <div key={t} className="flex min-w-[110px] flex-col rounded-xl border border-slate-200 bg-white px-4 py-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">{t} em campo</span>
                <span className="text-xl font-bold text-slate-700">{totals[t]}</span>
              </div>
            ))}
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  <th className={th}>Técnico</th>
                  <th className={th}>CTO</th>
                  <th className={th}>CEP</th>
                  <th className={th}>CEO</th>
                  <th className={th}>Total</th>
                  <th className={cn(th, 'text-right')}>Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {techs.length === 0 ? (
                  <tr>
                    <td className={cn(td, 'text-slate-400')} colSpan={6}>
                      Nenhum técnico cadastrado.
                    </td>
                  </tr>
                ) : (
                  techs.map((u) => {
                    const s = u.stock || { CTO: 0, CEP: 0, CEO: 0 };
                    const total = s.CTO + s.CEP + s.CEO;
                    return (
                      <tr key={u.id} className="hover:bg-slate-50">
                        <td className={cn(td, 'font-medium text-slate-800')}>{u.username}</td>
                        {TYPES.map((t) => (
                          <td key={t} className={td}>
                            <span className={cn('font-semibold', (s[t] || 0) > 0 ? 'text-slate-700' : 'text-red-500')}>{s[t] || 0}</span>
                          </td>
                        ))}
                        <td className={cn(td, 'font-bold text-slate-800')}>{total}</td>
                        <td className={cn(td, 'text-right')}>
                          <Button
                            size="sm"
                            onClick={() => {
                              setStockEntry({ CTO: '', CEP: '', CEO: '' });
                              setStockTarget(u);
                            }}
                          >
                            <FiPlus size={15} />
                            <span>Lançar</span>
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={fUser} onChange={(e) => setFUser(e.target.value)} className="h-9 w-44">
              <option value="">Todos os técnicos</option>
              {techs.map((u) => (
                <option key={u.id} value={u.username}>
                  {u.username}
                </option>
              ))}
            </Select>
            <Select value={fType} onChange={(e) => setFType(e.target.value)} className="h-9 w-32">
              <option value="">Todos os tipos</option>
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
            <Select value={fReason} onChange={(e) => setFReason(e.target.value)} className="h-9 w-40">
              <option value="">Todos os motivos</option>
              <option value="entrada">Entrada</option>
              <option value="montagem">Montagem</option>
              <option value="ajuste">Ajuste</option>
            </Select>
            <label className="flex items-center gap-1 text-xs font-medium text-slate-500">
              De
              <input
                type="date"
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-blue-500"
                value={fFrom}
                onChange={(e) => setFFrom(e.target.value)}
              />
            </label>
            <label className="flex items-center gap-1 text-xs font-medium text-slate-500">
              Até
              <input
                type="date"
                className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm text-slate-700 outline-none focus:border-blue-500"
                value={fTo}
                onChange={(e) => setFTo(e.target.value)}
              />
            </label>
            {(fFrom || fTo) && (
              <button
                className="text-xs font-medium text-blue-600 hover:underline"
                onClick={() => {
                  setFFrom('');
                  setFTo('');
                }}
              >
                limpar datas
              </button>
            )}
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {filteredMovements.length} movimentações
            </span>
          </div>

          {/* Resumo do período */}
          <div className="flex flex-wrap gap-2">
            <div className="flex min-w-[120px] flex-col rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-emerald-600">Entradas</span>
              <span className="text-xl font-bold text-emerald-700">+{summary.entradas}</span>
            </div>
            <div className="flex min-w-[200px] flex-1 flex-col rounded-xl border border-red-200 bg-red-50 px-4 py-2">
              <span className="text-[11px] font-medium uppercase tracking-wide text-red-500">Consumo (montagens)</span>
              <div className="flex items-baseline gap-3">
                <span className="text-xl font-bold text-red-600">{summary.consumo.total}</span>
                <span className="text-xs text-red-500">
                  CTO {summary.consumo.CTO} · CEP {summary.consumo.CEP} · CEO {summary.consumo.CEO}
                </span>
              </div>
            </div>
            {summary.ajustes !== 0 && (
              <div className="flex min-w-[110px] flex-col rounded-xl border border-slate-200 bg-white px-4 py-2">
                <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Ajustes</span>
                <span className="text-xl font-bold text-slate-700">
                  {summary.ajustes > 0 ? '+' : ''}
                  {summary.ajustes}
                </span>
              </div>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-50">
                <tr>
                  <th className={th}>Data</th>
                  <th className={th}>Técnico</th>
                  <th className={th}>Tipo</th>
                  <th className={th}>Qtd</th>
                  <th className={th}>Motivo</th>
                  <th className={th}>Caixa</th>
                  <th className={th}>Saldo</th>
                  <th className={th}>Lançado por</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMovements.length === 0 ? (
                  <tr>
                    <td className={cn(td, 'text-slate-400')} colSpan={8}>
                      {loading ? 'Carregando…' : 'Nenhuma movimentação.'}
                    </td>
                  </tr>
                ) : (
                  filteredMovements.slice(0, 800).map((m) => (
                    <tr key={m.id} className="hover:bg-slate-50">
                      <td className={cn(td, 'text-slate-500')}>{fmtDateTime(m.createdAt)}</td>
                      <td className={cn(td, 'font-medium text-slate-800')}>{m.username || '—'}</td>
                      <td className={td}>
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-600">{m.type}</span>
                      </td>
                      <td className={cn(td, 'font-bold', m.delta > 0 ? 'text-emerald-600' : 'text-red-600')}>
                        {m.delta > 0 ? '+' : ''}
                        {m.delta}
                      </td>
                      <td className={td}>{REASON_LABEL[m.reason] || m.reason}</td>
                      <td className={cn(td, 'font-mono text-xs')}>{m.boxId || '—'}</td>
                      <td className={td}>{m.balanceAfter}</td>
                      <td className={cn(td, 'text-slate-500')}>{m.changedBy || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {stockTarget && (
        <ModalBackdrop onClose={() => setStockTarget(null)}>
          <div
            className="flex max-h-[90vh] w-full max-w-sm flex-col overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
          >
            <h3 className="text-lg font-bold text-slate-800">Lançar estoque</h3>
            <p className="mt-0.5 text-sm text-slate-500">
              Entrada de material para <span className="font-semibold text-slate-700">{stockTarget.username}</span> (soma ao saldo atual).
            </p>
            <div className="mt-3 flex gap-2">
              {TYPES.map((t) => (
                <span key={t} className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  {t} atual: {stockTarget.stock?.[t] ?? 0}
                </span>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3">
              {TYPES.map((t) => (
                <div key={t}>
                  <label className="text-xs font-semibold text-slate-600">+ {t}</label>
                  <TextInput
                    type="number"
                    inputMode="numeric"
                    placeholder="0"
                    value={stockEntry[t]}
                    onChange={(e) => setStockEntry((p) => ({ ...p, [t]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setStockTarget(null)}>
                Fechar
              </Button>
              <Button onClick={submitStock}>Lançar entrada</Button>
            </div>

            <div className="mt-5 border-t border-slate-100 pt-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Histórico recente</p>
              {targetMovements.length === 0 ? (
                <p className="text-sm text-slate-400">Sem movimentações ainda.</p>
              ) : (
                <div className="max-h-56 space-y-1.5 overflow-y-auto">
                  {targetMovements.map((m) => (
                    <div key={m.id} className="rounded-lg border border-slate-100 px-2.5 py-1.5">
                      <div className="flex items-center gap-2 text-xs">
                        <span className={cn('font-bold', m.delta > 0 ? 'text-emerald-600' : 'text-red-600')}>
                          {m.delta > 0 ? '+' : ''}
                          {m.delta} {m.type}
                        </span>
                        <span className="text-slate-500">
                          {REASON_LABEL[m.reason] || m.reason}
                          {m.boxId ? ` · ${m.boxId}` : ''}
                        </span>
                        <span className="ml-auto whitespace-nowrap text-slate-400">
                          {new Date(m.createdAt).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-400">
                        saldo {m.balanceAfter} · por {m.changedBy || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </ModalBackdrop>
      )}
    </div>
  );
}
